import { IonContent, IonPage, IonItem, IonInput, IonButton, IonImg, IonGrid, IonRow, IonCol, IonText, IonSpinner, IonIcon } from '@ionic/react';
import { logoApple, logoGoogle } from 'ionicons/icons';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';
import { useState, useRef, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import SmartDocsLogo from '../assets/SmartDocsTextLogoLight.png';
import { useAuth } from '../context/AuthContext';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import './SignIn.css';
import './AuthPages.css';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: { client_id: string; callback: (response: { credential: string }) => void }) => void;
          renderButton: (element: HTMLElement, options: object) => void;
        };
      };
    };
    AppleID?: {
      auth: {
        init: (config: {
          clientId: string;
          scope: string;
          redirectURI: string;
          usePopup: boolean;
          state?: string;
          nonce?: string;
        }) => void;
        signIn: () => Promise<{
          authorization?: { id_token?: string };
          user?: { name?: { firstName?: string; lastName?: string } };
        }>;
      };
    };
  }
}

const SignIn: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState<'google' | 'apple' | null>(null);
  const [googleButtonMounted, setGoogleButtonMounted] = useState(false);
  const emailInputRef = useRef<HTMLIonInputElement>(null);
  const passwordInputRef = useRef<HTMLIonInputElement>(null);
  const googleButtonRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const location = useLocation();
  const successMessage = (location.state as { message?: string } | undefined)?.message || '';
  const { login, loginWithSSO, isAuthenticated, isInitialized } = useAuth();
  const { flags } = useFeatureFlags();
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
  const googleIosClientId = import.meta.env.VITE_GOOGLE_IOS_CLIENT_ID as string | undefined;
  const appleClientId = import.meta.env.VITE_APPLE_CLIENT_ID as string | undefined;
  const [appleReady, setAppleReady] = useState(false);

  const isIosNative = Capacitor.getPlatform() === 'ios' && Capacitor.isNativePlatform();
  const shouldShowSso = !!flags?.contractor_app_sso;

  // Check if user is already logged in and redirect accordingly.
  // Do not redirect when we have a success message (e.g. from password reset)
  // so the user can see the message and sign in with the new password.
  useEffect(() => {
    if (!isInitialized || !isAuthenticated) return;
    const state = location.state as { message?: string; from?: { pathname?: string } } | undefined;
    if (state?.message) return;
    const from = state?.from?.pathname || '/';
    history.replace(from);
  }, [isInitialized, isAuthenticated, history, location.state]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const originalStyle = Style.Light;

    const setDarkStatusBar = async () => {
      try {
        await StatusBar.setStyle({ style: Style.Dark });
      } catch (error) {
        console.warn('[SignIn] Unable to update status bar style:', error);
      }
    };

    const restoreStatusBar = async () => {
      try {
        await StatusBar.setStyle({ style: originalStyle });
      } catch (error) {
        console.warn('[SignIn] Unable to restore status bar style:', error);
      }
    };

    void setDarkStatusBar();

    return () => {
      void restoreStatusBar();
    };
  }, []);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
      const from = (location.state as any)?.from?.pathname || '/';
      history.replace(from);
    } catch (err) {
      const error = err as { message: string };
      setError(error.message || 'Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleCredential = useRef<(credential: string) => void>(() => {});

  useEffect(() => {
    if (!googleClientId || !isInitialized || !shouldShowSso) return;
    handleGoogleCredential.current = async (credential: string) => {
      setError('');
      setSsoLoading('google');
      try {
        await loginWithSSO(credential, 'google');
        const from = (location.state as any)?.from?.pathname || '/';
        history.replace(from);
      } catch (err: any) {
        setError(err?.message || 'Google Sign-In failed');
      } finally {
        setSsoLoading(null);
      }
    };
  }, [googleClientId, isInitialized, shouldShowSso, loginWithSSO, location.state, history]);

  useEffect(() => {
    if (!googleClientId || !googleButtonMounted || !googleButtonRef.current || !shouldShowSso) return;
    if (isIosNative) return;
    const mount = googleButtonRef.current;
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (!window.google?.accounts?.id || !mount) return;
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: (response: { credential: string }) => {
          handleGoogleCredential.current(response.credential);
        },
      });
      const containerWidth = mount.offsetWidth || mount.parentElement?.clientWidth || 320;
      window.google.accounts.id.renderButton(mount, {
        type: 'standard',
        theme: 'filled_blue',
        size: 'large',
        text: 'continue_with',
        width: containerWidth,
      });
    };
    document.body.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
      if (mount?.innerHTML) mount.innerHTML = '';
    };
  }, [googleClientId, googleButtonMounted, shouldShowSso, isIosNative]);

  const handleGoogleSignInNative = async () => {
    if (!isIosNative) return;
    setError('');
    setSsoLoading('google');
    try {
      const { GoogleAuth } = await import('@codetrix-studio/capacitor-google-auth');
      const iosClientId = googleIosClientId || googleClientId;
      if (!googleClientId) {
        throw new Error('Google Sign-In is not configured. Set VITE_GOOGLE_CLIENT_ID.');
      }
      if (!iosClientId) {
        throw new Error('For iOS, set VITE_GOOGLE_IOS_CLIENT_ID (iOS client ID from Google Console).');
      }
      GoogleAuth.initialize({
        clientId: iosClientId,
        scopes: ['profile', 'email'],
      });
      // Defer signIn so the native bridge/viewController is ready (avoids Plugin.swift crash)
      const user = await new Promise<{ authentication?: { idToken?: string }; givenName?: string; familyName?: string; name?: string }>((resolve, reject) => {
        const run = () => {
          GoogleAuth.signIn().then(resolve).catch(reject);
        };
        if (typeof requestAnimationFrame !== 'undefined') {
          requestAnimationFrame(() => run());
        } else {
          setTimeout(run, 100);
        }
      });
      const idToken = user?.authentication?.idToken;
      if (!idToken) {
        throw new Error('Google did not return an identity token.');
      }
      const fullName = [user?.givenName, user?.familyName].filter(Boolean).join(' ').trim() || user?.name || undefined;
      await loginWithSSO(idToken, 'google', { name: fullName });
      const from = (location.state as any)?.from?.pathname || '/';
      history.replace(from);
    } catch (err: any) {
      if (err?.message === 'User cancelled') {
        setError('');
      } else {
        setError(err?.message || 'Google Sign-In failed');
      }
    } finally {
      setSsoLoading(null);
    }
  };

  useEffect(() => {
    if (!appleClientId || Capacitor.isNativePlatform() || !shouldShowSso) {
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      try {
        window.AppleID?.auth.init({
          clientId: appleClientId,
          scope: 'name email',
          redirectURI: `${window.location.origin}/signin`,
          usePopup: true,
          state: `smartdocs-${Date.now()}`,
        });
        setAppleReady(true);
      } catch (initError) {
        console.error('[SignIn] Failed to initialize Apple Sign-In', initError);
      }
    };
    document.body.appendChild(script);
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [appleClientId, shouldShowSso]);

  const handleAppleSignIn = async () => {
    setError('');
    setSsoLoading('apple');
    try {
      let idToken = '';
      let fullName = '';

      if (Capacitor.getPlatform() === 'ios') {
        const plugin = await import('@capacitor-community/apple-sign-in');
        const result: any = await (plugin as any).SignInWithApple.authorize({
          scopes: 'email name',
          state: `smartdocs-${Date.now()}`,
        });
        const response = result?.response || result;
        idToken = response?.identityToken || response?.id_token || '';
        const givenName = response?.givenName || response?.name?.firstName || '';
        const familyName = response?.familyName || response?.name?.lastName || '';
        fullName = `${givenName} ${familyName}`.trim();
      } else {
        if (!appleClientId) {
          throw new Error('Apple Sign-In is not configured. Set VITE_APPLE_CLIENT_ID.');
        }
        if (!appleReady || !window.AppleID?.auth) {
          throw new Error('Apple Sign-In is not ready yet. Try again in a moment.');
        }
        const response = await window.AppleID.auth.signIn();
        idToken = response?.authorization?.id_token || '';
        fullName = `${response?.user?.name?.firstName || ''} ${response?.user?.name?.lastName || ''}`.trim();
      }

      if (!idToken) {
        throw new Error('Apple did not return a valid identity token.');
      }

      await loginWithSSO(idToken, 'apple', { name: fullName || undefined });
      const from = (location.state as any)?.from?.pathname || '/';
      history.replace(from);
    } catch (err: any) {
      const message = err?.message || 'Apple Sign-In failed';
      setError(message);
    } finally {
      setSsoLoading(null);
    }
  };

  // Show loading spinner while auth is initializing
  if (!isInitialized) {
    return (
    <IonPage className="sign-in-page">
      <IonContent className="ion-padding ion-text-center" fullscreen={false}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <IonSpinner name="dots" />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage className="sign-in-page">
      <IonContent className="sign-in-content" fullscreen={false}>
        <div className="sign-in-wrapper">
          <IonGrid className="ion-text-center sign-in-grid">
            <IonRow className="ion-justify-content-center">
              <IonCol size="12" sizeMd="6" sizeLg="4">
                <div className="logo-container">
                  <IonImg src={SmartDocsLogo} alt="SmartDocs Logo" className="logo" />
                </div>

                <form onSubmit={handleSignIn} className="sign-in-form">
                  {successMessage && (
                    <IonText color="success" className="success-text">
                      {successMessage}
                    </IonText>
                  )}

                  <IonItem className="custom-input no-border">
                    <IonInput
                      ref={emailInputRef}
                      type="email"
                      value={email}
                      onIonInput={e => setEmail(e.detail.value!)}
                      required
                      className="input-field no-autofill-border"
                      autocomplete="email"
                      label="Email"
                      labelPlacement="floating"
                      color="light"
                    />
                  </IonItem>

                  <IonItem className="custom-input no-border">
                    <IonInput
                      ref={passwordInputRef}
                      type="password"
                      value={password}
                      onIonInput={e => setPassword(e.detail.value!)}
                      required
                      className="input-field no-autofill-border"
                      autocomplete="current-password"
                      label="Password"
                      labelPlacement="floating"
                      color="light"
                    />
                  </IonItem>

                  {error && (
                    <IonText color="danger" className="error-text">
                      {error}
                    </IonText>
                  )}

                  <IonButton
                    expand="block"
                    type="submit"
                    className="sign-in-button"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <div className="loading-container">
                        <IonSpinner name="crescent" />
                        <span className="loading-text">Signing in...</span>
                      </div>
                    ) : (
                      'Sign In'
                    )}
                  </IonButton>

                  <IonButton
                    type="button"
                    fill="clear"
                    className="auth-link-button"
                    onClick={() => history.push('/forgot-password')}
                  >
                    Forgot password?
                  </IonButton>

                  {shouldShowSso && (
                    <>
                      <div style={{ marginTop: '16px', marginBottom: '8px', fontSize: '14px', color: 'var(--ion-color-medium)' }}>
                        — or —
                      </div>

                      {isIosNative ? (
                        (googleClientId || googleIosClientId) ? (
                          <div className="apple-signin-wrapper">
                            <IonButton
                              expand="block"
                              className="sign-in-button sign-in-button-sso"
                              disabled={ssoLoading === 'google'}
                              onClick={handleGoogleSignInNative}
                            >
                              <IonIcon icon={logoGoogle} slot="start" />
                              {ssoLoading === 'google' ? 'Signing in with Google...' : 'Sign in with Google'}
                            </IonButton>
                          </div>
                        ) : (
                          <IonButton expand="block" disabled className="sign-in-button sign-in-button-sso">
                            Sign in with Google (set VITE_GOOGLE_IOS_CLIENT_ID for iOS)
                          </IonButton>
                        )
                      ) : (
                        <>
                          {googleClientId ? (
                            <div
                              ref={(el) => { (googleButtonRef as React.MutableRefObject<HTMLDivElement | null>).current = el; if (el) setGoogleButtonMounted(true); }}
                              id="google-signin-btn"
                              className="google-signin-wrapper"
                            />
                          ) : (
                            <IonButton expand="block" disabled className="sign-in-button sign-in-button-sso">
                              Sign in with Google (set VITE_GOOGLE_CLIENT_ID)
                            </IonButton>
                          )}
                        </>
                      )}
                      <div className="apple-signin-wrapper">
                        <IonButton
                          expand="block"
                          className="sign-in-button sign-in-button-sso"
                          disabled={ssoLoading === 'apple'}
                          onClick={handleAppleSignIn}
                        >
                          <IonIcon icon={logoApple} slot="start" />
                          {ssoLoading === 'apple' ? 'Signing in with Apple...' : 'Sign in with Apple'}
                        </IonButton>
                      </div>
                    </>
                  )}
                </form>
              </IonCol>
            </IonRow>
          </IonGrid>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default SignIn;
